// "Raise with the project leads" confirm dialog — Story #5305 Stream 14b
// (Task #5318), design §6.4: "the confirm step quotes the verbatim
// recipient text and lists the recipients before the one send-once-per-
// group click." Admin/billing-owner only (enforced server-side; the
// workbench never even offers this button to a caller below the floor —
// see OverlapWorkbench.tsx).
import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2, Send, X } from 'lucide-react'
import { notifyOverlapLeads, previewNotifyOverlapLeads } from '../../services/api'
import { OVERLAP_STALE_GROUP_MESSAGE } from '../../lib/whitespace'

interface OverlapNotifyLeadsModalProps {
  groupId: string
  aggregatorId: string | null
  memberCount: number
  onClose: () => void
}

export default function OverlapNotifyLeadsModal({
  groupId, aggregatorId, memberCount, onClose,
}: OverlapNotifyLeadsModalProps) {
  const qc = useQueryClient()

  const previewQuery = useQuery({
    queryKey: ['overlap-notify-preview', aggregatorId, groupId],
    queryFn: () => previewNotifyOverlapLeads(groupId, aggregatorId),
  })

  const sendMutation = useMutation({
    mutationFn: () => notifyOverlapLeads(groupId, aggregatorId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portfolio-overlap-group', aggregatorId, groupId] })
    },
  })

  // Esc closes the dialog, same convention every other modal in this app uses.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const alreadySent = (sendMutation.error as { response?: { status?: number } })?.response?.status === 409
  const isStale = [previewQuery.error, sendMutation.error].some(
    (e) => (e as { response?: { status?: number } })?.response?.status === 404,
  )

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5" data-testid="overlap-notify-modal">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-sm font-bold text-iflow-900">Raise with the {memberCount} project leads</h3>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        {isStale ? (
          <p className="text-xs text-gray-600" data-testid="overlap-notify-stale">{OVERLAP_STALE_GROUP_MESSAGE}</p>
        ) : previewQuery.isLoading ? (
          <p className="text-xs text-gray-500">Loading the message and recipients…</p>
        ) : previewQuery.isError ? (
          <p className="text-xs text-red-600" role="alert">
            {(previewQuery.error as Error)?.message ?? 'Failed to load the notify preview.'}
          </p>
        ) : sendMutation.isSuccess ? (
          <div data-testid="overlap-notify-sent">
            <p className="text-xs text-green-700 font-semibold">Sent — {sendMutation.data.notified_owner_ids.length} lead(s) notified.</p>
            <p className="text-[11px] text-gray-500 mt-1">This group has now been raised once — a second send is blocked.</p>
          </div>
        ) : alreadySent ? (
          <p className="text-xs text-gray-600" data-testid="overlap-notify-dedup">
            This group has already been raised with its leads — a group can only be raised once.
          </p>
        ) : (
          <>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Exact message to be sent</p>
            <div className="p-2.5 bg-sky-500/5 rounded-lg text-xs text-gray-800" data-testid="overlap-notify-message">
              "{previewQuery.data?.message}"
            </div>
            {previewQuery.data?.floor_capped && (
              <div className="flex items-start gap-1.5 mt-2 text-[11px] text-amber-700" data-testid="overlap-notify-floor-capped">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Limited documentary evidence — treat as a lead, not a verdict.</span>
              </div>
            )}
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mt-3 mb-1">Recipients</p>
            <ul className="space-y-0.5" data-testid="overlap-notify-recipients">
              {previewQuery.data?.recipients.map((r) => (
                <li key={r.project_id} className="text-xs text-gray-700">{r.project_name ?? r.project_id}</li>
              ))}
            </ul>
            <p className="text-[10.5px] text-gray-500 mt-3">
              Leads are asked privately — nothing about the other projects is shared with them. Each lead
              sees only this exact message. Once sent, this cannot be undone or repeated.
            </p>
            {sendMutation.isError && !alreadySent && (
              <p className="text-xs text-red-600 mt-2" role="alert">
                {(sendMutation.error as Error)?.message ?? 'Failed to send.'}
              </p>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={onClose} className="btn text-xs px-3 py-1.5">Cancel</button>
              <button
                type="button"
                onClick={() => sendMutation.mutate()}
                disabled={sendMutation.isPending || !previewQuery.data}
                data-testid="overlap-notify-confirm-send"
                className="btn pri text-xs px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
              >
                {sendMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {sendMutation.isPending ? 'Sending…' : 'Send once'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
